/**
 * DynamicConnectorForm -- main orchestrator for schema-driven connector forms.
 *
 * Fetches the connector type schema from the API, computes field visibility
 * based on current form values, groups and sorts fields, and renders them
 * via DynamicField inside FieldGroupSection containers.
 *
 * This component is the single entry point for rendering any connector's
 * configuration form -- no per-connector form code required.
 */
import { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { Loader2, AlertCircle, RefreshCw, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useConnectorTypeSchema } from '../../../../hooks/useConnectorTypes';
import { getVisibleFieldNames } from '../../../../lib/validation/fieldVisibility';
import { DynamicField } from './DynamicField';
import { FieldErrorBoundary } from './FieldErrorBoundary';
import { FieldGroupSection } from './FieldGroupSection';
import { LiveRegion } from './LiveRegion';
import type { Advertiser } from '../../form/LoadAdvertisersButton';
import type { ConnectorFieldGroup, ConnectorFieldSchema } from '../../../../types/connector-schema';

interface DynamicConnectorFormProps {
  /** Connector type key, e.g. "google_ads", "s3_csv" */
  connectorType: string;
  /** Current form values keyed by field name */
  values: Record<string, unknown>;
  /** Called with the full values object whenever any field changes */
  onChange: (values: Record<string, unknown>) => void;
  /** Validation errors from an external source (e.g. server-side) */
  externalErrors?: Record<string, string>;
  /** Flags indicating which credential fields have stored values */
  credentialFlags?: Record<string, boolean>;
}

interface GroupWithFields {
  group: ConnectorFieldGroup;
  fields: ConnectorFieldSchema[];
}

export function DynamicConnectorForm({
  connectorType,
  values,
  onChange,
  externalErrors,
  credentialFlags,
}: DynamicConnectorFormProps) {
  // ── 1. Fetch schema ────────────────────────────────────────────────
  const {
    data: schema,
    isLoading,
    error,
    refetch,
  } = useConnectorTypeSchema(connectorType);

  const [isRetrying, setIsRetrying] = useState(false);

  // Runtime cache of results produced by LOAD_ADVERTISERS_BUTTON fields.
  // Keyed by loader field name (e.g. "load_advertisers"); companion
  // MULTI_SELECT_CHECKBOX_DYNAMIC fields read these via their
  // ``loader_field`` pointer. Cleared automatically when the host form
  // re-mounts (e.g. when opening a different connector's config modal).
  const [loaderResults, setLoaderResults] = useState<
    Record<string, Advertiser[]>
  >({});

  const handleLoaderResult = useCallback(
    (fieldName: string, advertisers: Advertiser[]) => {
      setLoaderResults((prev) => ({ ...prev, [fieldName]: advertisers }));
    },
    []
  );

  // ── 1b. Seed unset field values from schema defaults ──────────────
  // When the schema loads, fields whose names are absent from the
  // passed-in `values` object are seeded from `field.default`. Existing
  // keys (from edit flows or parent-supplied state) are never overwritten.
  // `null` / `undefined` defaults are ignored; legitimate defaults like
  // empty arrays, 0, and false ARE applied. A ref guards against
  // re-entry after the parent re-renders with the seeded values.
  const seededSchemaRef = useRef<unknown>(null);
  useEffect(() => {
    if (!schema) return;
    if (seededSchemaRef.current === schema) return;

    const seed: Record<string, unknown> = {};
    let didSeed = false;
    for (const field of schema.fields) {
      if (field.name in values) continue;
      // Backend can return arrays/objects as defaults even though the TS
      // type is narrower; read via a cast instead of a type assertion on
      // the schema field itself.
      const def = (field as { default?: unknown }).default;
      if (def === undefined || def === null) continue;
      seed[field.name] = def;
      didSeed = true;
    }

    // Mark this schema as processed even if no seeding was needed so
    // the effect short-circuits on subsequent renders.
    seededSchemaRef.current = schema;

    if (didSeed) {
      onChange({ ...values, ...seed });
    }
  }, [schema, values, onChange]);

  // ── 2. Compute visible fields ──────────────────────────────────────
  const visibleFields = useMemo(
    () => (schema ? getVisibleFieldNames(schema.fields, values) : new Set<string>()),
    [schema, values]
  );

  // ── 3. Group and sort fields ───────────────────────────────────────
  const groups: GroupWithFields[] = useMemo(() => {
    if (!schema) return [];

    const sortedGroups = [...schema.field_groups].sort((a, b) => a.order - b.order);

    return sortedGroups
      .map((group) => ({
        group,
        fields: schema.fields
          .filter((f) => f.group === group.name && visibleFields.has(f.name))
          .sort((a, b) => a.order - b.order),
      }))
      .filter(({ fields }) => fields.length > 0);
  }, [schema, visibleFields]);

  // ── 3b. OAuth gate ─────────────────────────────────────────────────
  // When the schema has a required oauth_button field, hide every group
  // ordered after the OAuth field's group until that field has a value.
  // (#260 D2 -- Authentication is a gate that unlocks the rest of the form.)
  const gate = useMemo(() => {
    if (!schema) return { gatedGroups: [] as GroupWithFields[], showGatedNote: false };
    const authField = schema.fields.find(
      (f) => f.type === 'oauth_button' && f.required
    );
    // Satisfied if the OAuth field has a value in the form, OR a credential is
    // already stored server-side (edit flow -- creds aren't sent to the client,
    // only flagged). Without the credentialFlags check, editing an already-
    // connected connector would wrongly hide its account selection.
    const satisfied = authField
      ? !!values[authField.name] || !!credentialFlags?.[`has_${authField.name}`]
      : true;
    if (!authField || satisfied) {
      return { gatedGroups: groups, showGatedNote: false };
    }
    // Gate only groups whose fields can't populate without a token -- i.e. groups
    // containing an oauth_populated_dropdown -- excluding the OAuth button's own
    // group (so the Connect button itself is never hidden). Static fields
    // (resources, reporting windows) stay visible. (#260 D2, narrowed.)
    const gatedGroupNames = new Set(
      schema.fields
        .filter(
          (f) =>
            f.type === 'oauth_populated_dropdown' &&
            f.group &&
            f.group !== authField.group
        )
        .map((f) => f.group as string)
    );
    const visibleGroups = groups.filter(({ group }) => !gatedGroupNames.has(group.name));
    const showGatedNote = groups.some(({ group }) => gatedGroupNames.has(group.name));
    return { gatedGroups: visibleGroups, showGatedNote };
  }, [schema, groups, values, credentialFlags]);

  // Also collect any visible fields that have no group (or an unknown group)
  const ungroupedFields = useMemo(() => {
    if (!schema) return [];
    const knownGroups = new Set(schema.field_groups.map((g) => g.name));
    return schema.fields
      .filter((f) => (!f.group || !knownGroups.has(f.group)) && visibleFields.has(f.name))
      .sort((a, b) => a.order - b.order);
  }, [schema, visibleFields]);

  // ── 4. Handle field changes ────────────────────────────────────────
  const handleFieldChange = useCallback(
    (fieldName: string, value: unknown) => {
      onChange({ ...values, [fieldName]: value });
    },
    [values, onChange]
  );

  // Batch update: set multiple fields atomically (avoids stale-closure races)
  const handleBatchFieldChange = useCallback(
    (updates: Record<string, unknown>) => {
      onChange({ ...values, ...updates });
    },
    [values, onChange]
  );

  // ── 5. Screen reader announcements ────────────────────────────────
  // Adjust state during render instead of in an effect
  // (https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes).
  // Each block below mirrors exactly one of the original effects' dependency
  // arrays via a previous-value state mirror, so `announcement` still updates
  // (or is left untouched, matching the original's silent no-op branches)
  // only when that specific dependency changes. Each guard converges on the
  // very next render once its mirror catches up.
  const [announcement, setAnnouncement] = useState('');

  const [prevIsLoadingForAnnounce, setPrevIsLoadingForAnnounce] = useState(isLoading);
  if (isLoading !== prevIsLoadingForAnnounce) {
    setPrevIsLoadingForAnnounce(isLoading);
    if (isLoading) {
      setAnnouncement('Loading connector configuration');
    }
  }

  const [prevExternalErrors, setPrevExternalErrors] = useState(externalErrors);
  if (externalErrors !== prevExternalErrors) {
    setPrevExternalErrors(externalErrors);
    if (externalErrors) {
      const count = Object.keys(externalErrors).length;
      if (count > 0) {
        setAnnouncement(`Form has ${count} error${count === 1 ? '' : 's'}`);
      }
    }
  }

  // ── 6. Render ──────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        <span>Loading configuration...</span>
      </div>
    );
  }

  if (error) {
    const handleRetry = async () => {
      setIsRetrying(true);
      try {
        await refetch();
      } finally {
        setIsRetrying(false);
      }
    };

    return (
      <div className="flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-4 text-destructive">
        <AlertCircle className="h-5 w-5 flex-shrink-0" />
        <div className="flex-1">
          <p className="font-medium">Failed to load configuration schema</p>
          <p className="text-sm mt-1">
            {error instanceof Error ? error.message : 'An unexpected error occurred.'}
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={handleRetry}
            disabled={isRetrying}
          >
            {isRetrying ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!schema) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Ungrouped fields rendered first */}
      {ungroupedFields.length > 0 && (
        <div className="space-y-4">
          {ungroupedFields.map((field) => (
            <FieldErrorBoundary key={field.name} fieldName={field.name}>
              <DynamicField
                field={field}
                value={values[field.name]}
                onChange={(val) => handleFieldChange(field.name, val)}
                onBatchFieldChange={handleBatchFieldChange}
                error={externalErrors?.[field.name]}
                allValues={values}
                connectorType={connectorType}
                credentialFlags={credentialFlags}
                loaderResults={loaderResults}
                onLoaderResult={handleLoaderResult}
              />
            </FieldErrorBoundary>
          ))}
        </div>
      )}

      {/* Grouped fields (gated: groups after Authentication appear once OAuth completes) */}
      {gate.gatedGroups.map(({ group, fields }) => (
        <FieldGroupSection key={group.name} group={group}>
          {fields.map((field) => (
            <FieldErrorBoundary key={field.name} fieldName={field.name}>
              <DynamicField
                field={field}
                value={values[field.name]}
                onChange={(val) => handleFieldChange(field.name, val)}
                onBatchFieldChange={handleBatchFieldChange}
                error={externalErrors?.[field.name]}
                allValues={values}
                connectorType={connectorType}
                credentialFlags={credentialFlags}
                loaderResults={loaderResults}
                onLoaderResult={handleLoaderResult}
              />
            </FieldErrorBoundary>
          ))}
        </FieldGroupSection>
      ))}

      {gate.showGatedNote && (
        <div className="flex items-center gap-2 rounded-lg border border-dashed px-3.5 py-3 text-sm text-muted-foreground">
          <Lock className="h-4 w-4 flex-shrink-0" />
          <span>Account selection appears once you connect.</span>
        </div>
      )}

      <LiveRegion message={announcement} />
    </div>
  );
}
